import { useState, useMemo, useCallback } from 'react';
import { CustomerData } from './types';
import { isPendingUser } from './stats/customerUtils';

export const useCustomerListState = (allCustomers: CustomerData[]) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [planFilter, setPlanFilter] = useState<string | null>(null);
  
  const customerStats = useMemo(() => {
    const totalUsers = allCustomers.length;
    
    const pendingUsers = allCustomers.filter(c => isPendingUser(c)).length;
    const monthlyUsers = allCustomers.filter(c => c.plan === "monthly" && c.status !== "canceled").length;
    const annualUsers = allCustomers.filter(c => c.plan === "annual" && c.status !== "canceled").length;
    const lifetimeUsers = allCustomers.filter(c => c.plan === "lifetime").length;
    const cancelledUsers = allCustomers.filter(c => c.status === "canceled").length;
    
    console.log('Customer Stats Diagnostic:', {
      totalUsers,
      pendingUsers,
      sampleCustomers: allCustomers.slice(0, 5).map(c => ({
        id: c.id,
        plan: c.plan,
        status: c.status,
        createdAt: c.created_at
      }))
    });
    
    return {
      totalUsers,
      pendingUsers,
      monthlyUsers,
      annualUsers,
      lifetimeUsers,
      cancelledUsers
    };
  }, [allCustomers]);
  
  const filteredCustomers = useMemo(() => {
    return allCustomers.filter(customer => {
      const matchesSearch = 
        searchTerm === '' || 
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      let matchesPlan = true;
      if (planFilter) {
        if (planFilter === 'pending') {
          matchesPlan = isPendingUser(customer);
        } else if (planFilter === 'canceled') {
          matchesPlan = customer.status === 'canceled';
        } else {
          matchesPlan = customer.plan === planFilter && customer.status !== 'canceled';
        }
      }
      
      return matchesSearch && matchesPlan;
    });
  }, [allCustomers, searchTerm, planFilter]);
  
  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      let valueA = a[sortColumn as keyof CustomerData];
      let valueB = b[sortColumn as keyof CustomerData];
      
      if (sortColumn === 'downloads' || sortColumn === 'pageViews' || sortColumn === 'tracksCount') {
        valueA = valueA || 0;
        valueB = valueB || 0;
      }
      
      if (valueA === valueB) return 0;
      
      if (sortColumn === 'created_at') {
        const dateA = new Date(valueA as string).getTime();
        const dateB = new Date(valueB as string).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      }
      
      return sortDirection === 'asc' 
        ? (valueA as number) - (valueB as number) 
        : (valueB as number) - (valueA as number);
    });
  }, [filteredCustomers, sortColumn, sortDirection]);
  
  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  }, [sortColumn, sortDirection]);
  
  return {
    sortedCustomers,
    searchTerm,
    setSearchTerm,
    handleSort,
    sortColumn,
    sortDirection,
    planFilter,
    setPlanFilter,
    customerStats
  };
};
